import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Swords, Target, Zap, AlertCircle, Loader2, Sparkles } from "lucide-react";
import type { NarrativeOpportunity } from "@/lib/campaign-draft";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface NarrativeGeneration {
  id: string;
  brandId: string;
  personaId: string;
  productId: string;
  status: string;
  errorMessage?: string | null;
  parsedOutputJson: {
    top_opportunities?: Array<{
      title: string;
      opportunity_type: string;
      core_tension: string;
      persona_reason: string;
      product_anchor: string;
      why_promising: string;
      strength: number;
    }>;
  } | null;
  completedAt: string | null;
  createdAt: string;
}

interface NarrativeOpportunityStepProps {
  brandId: string;
  productId: string | null;
  personaId: string | null;
  selectedOpportunity: NarrativeOpportunity | null;
  onSelect: (opp: NarrativeOpportunity) => void;
  hasProduct: boolean;
  hasPersona: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  conflict: "Conflict",
  identity: "Identity",
  outcome: "Outcome",
  trust: "Trust",
  life_fit: "Life Fit",
  worldview: "Worldview",
};

const TYPE_ICONS: Record<string, typeof Swords> = {
  conflict: Swords,
  identity: Zap,
  outcome: Target,
  trust: Zap,
  life_fit: Target,
  worldview: Swords,
};

function strengthColor(strength: number) {
  if (strength >= 9) return "text-primary";
  if (strength >= 7) return "text-foreground/70";
  return "text-muted-foreground";
}

const PROGRESS_MESSAGES = [
  "Analyzing persona–product intersections…",
  "Mapping worldview signals to product truths…",
  "Evaluating emotional drivers…",
  "Scoring narrative tension points…",
  "Ranking opportunity strength…",
  "Finalizing narrative opportunities…",
];

function InlineGeneratePanel({
  brandId,
  productId,
  personaId,
  initialGeneratingId = null,
  terminalGeneration = null,
}: {
  brandId: string;
  productId: string;
  personaId: string;
  initialGeneratingId?: string | null;
  terminalGeneration?: Pick<NarrativeGeneration, "id" | "status" | "errorMessage"> | null;
}) {
  const [generatingId, setGeneratingId] = useState<string | null>(initialGeneratingId);
  const [progressIndex, setProgressIndex] = useState(0);
  const [error, setError] = useState<string | null>(
    !initialGeneratingId && terminalGeneration &&
      (terminalGeneration.status === "failed" || terminalGeneration.status === "error")
      ? terminalGeneration.errorMessage?.trim() || "Generation failed. Please try again."
      : null,
  );
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const previousInitialGeneratingIdRef = useRef(initialGeneratingId);
  const previousTerminalSignatureRef = useRef(
    terminalGeneration
      ? `${terminalGeneration.id}:${terminalGeneration.status}:${terminalGeneration.errorMessage ?? ""}`
      : null,
  );
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    const signature = terminalGeneration
      ? `${terminalGeneration.id}:${terminalGeneration.status}:${terminalGeneration.errorMessage ?? ""}`
      : null;
    if (signature === previousTerminalSignatureRef.current) return;
    previousTerminalSignatureRef.current = signature;
    if (!terminalGeneration || terminalGeneration.id !== generatingId) return;

    setGeneratingId(null);
    if (terminalGeneration.status === "failed" || terminalGeneration.status === "error") {
      setError(
        terminalGeneration.errorMessage?.trim() || "Generation failed. Please try again.",
      );
    }
  }, [terminalGeneration, generatingId]);

  useEffect(() => {
    if (initialGeneratingId === previousInitialGeneratingIdRef.current) return;
    previousInitialGeneratingIdRef.current = initialGeneratingId;
    if (initialGeneratingId) {
      setGeneratingId(initialGeneratingId);
      setProgressIndex(0);
      setError(null);
    }
  }, [initialGeneratingId]);

  useEffect(() => {
    if (!generatingId) return;
    const interval = setInterval(() => {
      setProgressIndex((prev) =>
        prev < PROGRESS_MESSAGES.length - 1 ? prev + 1 : prev,
      );
    }, 4000);
    return () => clearInterval(interval);
  }, [generatingId]);

  useEffect(() => {
    if (!generatingId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch(`/api/narrative-generations/${generatingId}`, {
          credentials: "include",
        });
        if (!res.ok) return;
        const gen = await res.json();
        if (cancelled) return;

        if (gen.status === "completed") {
          setGeneratingId(null);
          queryClient.invalidateQueries({
            queryKey: [`/api/brands/${brandId}/narrative-generations`],
          });
          toast({
            title: "Narratives ready",
            description: "Narrative opportunities have been generated. Select one below.",
          });
        } else if (gen.status === "failed" || gen.status === "error") {
          cancelled = true;
          setGeneratingId(null);
          setError(gen.errorMessage?.trim() || "Generation failed. Please try again.");
          queryClient.invalidateQueries({
            queryKey: [`/api/brands/${brandId}/narrative-generations`],
          });
        }
      } catch {
        // keep polling
      }
    };

    const interval = setInterval(poll, 3000);
    poll();

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [generatingId, brandId, queryClient, toast]);

  const handleGenerate = async () => {
    if (submittingRef.current || generatingId) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    setProgressIndex(0);
    try {
      const res = await fetch(`/api/brands/${brandId}/narrative-generations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ personaId, productId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to start generation");
      }
      const gen = await res.json();
      setGeneratingId(gen.id);
    } catch (err: any) {
      setError(err.message || "Failed to start generation");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (generatingId) {
    return (
      <Card className="p-6 text-center space-y-4" data-testid="card-generating-narratives">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm font-medium">Generating Narratives</span>
        </div>
        <AnimatePresence mode="wait">
          <motion.p
            key={progressIndex}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="text-xs text-muted-foreground"
          >
            {PROGRESS_MESSAGES[progressIndex]}
          </motion.p>
        </AnimatePresence>
        <div className="w-full max-w-xs mx-auto bg-muted rounded-full h-1.5 overflow-hidden">
          <motion.div
            className="h-full bg-primary rounded-full"
            initial={{ width: "5%" }}
            animate={{ width: `${Math.min(15 + progressIndex * 15, 90)}%` }}
            transition={{ duration: 1 }}
          />
        </div>
        <p className="text-[11px] text-muted-foreground/60">Waiting for the analysis provider. You can return to this step later.</p>
      </Card>
    );
  }

  return (
    <Card className="p-6 text-center space-y-3" data-testid="card-no-narratives">
      <AlertCircle className="h-5 w-5 text-muted-foreground mx-auto" />
      <p className="text-sm text-muted-foreground">
        No narrative opportunities found for this product–persona combination.
      </p>
      <p className="text-xs text-muted-foreground/70">
        Generate a persona–product narrative analysis to discover high-conviction story angles for your campaign.
      </p>
      {error && (
        <p role="alert" data-testid="text-narrative-analysis-error" className="text-xs text-destructive">{error}</p>
      )}
      <Button
        onClick={handleGenerate}
        disabled={submitting}
        size="sm"
        className="gap-2"
        data-testid="button-generate-narratives"
      >
        <Sparkles className="h-3.5 w-3.5" />
        Generate Narrative Opportunities
      </Button>
    </Card>
  );
}

export function NarrativeOpportunityStep({
  brandId,
  productId,
  personaId,
  selectedOpportunity,
  onSelect,
  hasProduct,
  hasPersona,
}: NarrativeOpportunityStepProps) {
  const { data: generations, isLoading, isError } = useQuery<NarrativeGeneration[]>({
    queryKey: [`/api/brands/${brandId}/narrative-generations`],
    enabled: !!brandId && !!productId && !!personaId,
    refetchInterval: (query) => {
      const data = query.state.data as NarrativeGeneration[] | undefined;
      if (!data || !productId || !personaId) return false;
      const hasInProgress = data.some(
        (g) =>
          g.productId === productId &&
          g.personaId === personaId &&
          (g.status === "queued" || g.status === "running")
      );
      return hasInProgress ? 5000 : false;
    },
  });

  const matchingGeneration = useMemo(() => {
    if (!generations || !productId || !personaId) return null;
    return generations
      .filter(
        (g) =>
          g.productId === productId &&
          g.personaId === personaId &&
          g.status === "completed" &&
          g.parsedOutputJson?.top_opportunities
      )
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0] || null;
  }, [generations, productId, personaId]);

  const inProgress = useMemo(() => {
    if (!generations || !productId || !personaId) return null;
    return generations
      .filter(
        (g) =>
          g.productId === productId &&
          g.personaId === personaId &&
          (g.status === "queued" || g.status === "running")
      )
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0] || null;
  }, [generations, productId, personaId]);

  const opportunities: NarrativeOpportunity[] = useMemo(() => {
    if (!matchingGeneration?.parsedOutputJson?.top_opportunities) return [];
    return matchingGeneration.parsedOutputJson.top_opportunities.map(
      (opp, index) => ({
        id: `${matchingGeneration.id}-opp-${index}`,
        generationId: matchingGeneration.id,
        title: opp.title,
        opportunity_type: opp.opportunity_type,
        core_tension: opp.core_tension,
        persona_reason: opp.persona_reason,
        product_anchor: opp.product_anchor,
        why_promising: opp.why_promising,
        strength: opp.strength,
      })
    );
  }, [matchingGeneration]);

  const latestTerminalGeneration = useMemo(() => generations
    ?.filter(g => g.productId === productId && g.personaId === personaId &&
      (g.status === "completed" || g.status === "failed" || g.status === "error"))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0],
  [generations, productId, personaId]);

  return (
    <div className="space-y-6" data-testid="step-narrative-opportunity">
      <div>
        <h2
          className="text-xl font-bold tracking-tight"
          data-testid="text-step-title"
        >
          Narrative Opportunity
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Select the core narrative opportunity for this campaign
        </p>
      </div>

      {!hasProduct || !hasPersona ? (
        <Card className="p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Complete product and persona selection first to see narrative
            opportunities.
          </p>
        </Card>
      ) : isError ? (
        <Card className="p-6 text-center space-y-2">
          <AlertCircle className="h-5 w-5 text-destructive mx-auto" />
          <p className="text-sm text-muted-foreground">
            Failed to load narrative opportunities. Please try again.
          </p>
        </Card>
      ) : isLoading ? (
        <Card className="p-8 flex items-center justify-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">
            Loading narrative opportunities...
          </span>
        </Card>
      ) : opportunities.length === 0 ? (
        <InlineGeneratePanel
          key={`${brandId}:${productId}:${personaId}`}
          brandId={brandId}
          productId={productId!}
          personaId={personaId!}
          initialGeneratingId={inProgress?.id ?? null}
          terminalGeneration={latestTerminalGeneration ?? null}
        />
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            <div className="space-y-3">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {opportunities.length} Activated {opportunities.length === 1 ? "Opportunity" : "Opportunities"}
              </span>

              {opportunities.map((opp, index) => {
                const isSelected = selectedOpportunity?.id === opp.id;
                const TypeIcon =
                  TYPE_ICONS[opp.opportunity_type] || Zap;
                return (
                  <motion.div
                    key={opp.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08 }}
                  >
                    <Card
                      className={`p-4 cursor-pointer transition-colors ${
                        isSelected
                          ? "border-primary/40 bg-primary/[0.03] ring-1 ring-primary/20"
                          : "hover-elevate"
                      }`}
                      onClick={() => onSelect(opp)}
                      data-testid={`card-opportunity-${opp.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3
                              className="text-sm font-semibold"
                              data-testid={`text-opportunity-title-${opp.id}`}
                            >
                              {opp.title}
                            </h3>
                            <Badge variant="secondary" className="text-[10px]">
                              {TYPE_LABELS[opp.opportunity_type] ||
                                opp.opportunity_type}
                            </Badge>
                            <span
                              className={`text-xs font-semibold ${strengthColor(opp.strength)}`}
                            >
                              {opp.strength}/10
                            </span>
                          </div>

                          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">
                            {opp.core_tension}
                          </p>

                          <div className="grid grid-cols-2 gap-2 mt-3">
                            <div className="flex items-start gap-1.5">
                              <Swords className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                              <div>
                                <span className="text-[10px] text-muted-foreground">
                                  Persona Reason
                                </span>
                                <p className="text-[11px] line-clamp-2">
                                  {opp.persona_reason}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-1.5">
                              <Target className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                              <div>
                                <span className="text-[10px] text-muted-foreground">
                                  Product Anchor
                                </span>
                                <p className="text-[11px] line-clamp-2">
                                  {opp.product_anchor}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {isSelected && (
                          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {selectedOpportunity && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card
                  className="p-4 border-primary/20 bg-primary/[0.02]"
                  data-testid="card-selected-opportunity"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-semibold">
                      Selected Narrative Opportunity
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedOpportunity.why_promising}
                  </p>
                  <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground">
                    <Zap className="h-3 w-3" />
                    {TYPE_LABELS[selectedOpportunity.opportunity_type] ||
                      selectedOpportunity.opportunity_type}{" "}
                    opportunity — Strength {selectedOpportunity.strength}/10
                  </div>
                </Card>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}
